// React
import React, { useEffect, useState } from 'react';

// Third-party libraries
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { FullStory } from '@fullstory/browser';
import { ConfigProvider, message, theme as antdThemeAlgorithms } from 'antd';
import { useTranslation } from 'react-i18next';
import enUS from 'antd/locale/en_US';
import deDE from 'antd/locale/de_DE';
import jaJP from 'antd/locale/ja_JP';
import frFR from 'antd/locale/fr_FR';
import koKR from 'antd/locale/ko_KR';

// Hooks (barreled via @/hooks; useToken lives in common/helper)
import { useToken } from './common/helper';
import {
  useAICallStatusSocket,
  useIdleLogout,
  useCrossTabStorageSync,
  useAppearanceSync,
  useProfileQuery,
} from '@/hooks';

// Routes / route config
import {
  PrivateRoutes,
  PublicRoutes,
  ForgotPasswordRoutes,
  PublicRouteElements,
} from './routes';
import { AppRoutes } from './appRoutes';

// Layout
import DashboardLayout from './layout/index';
import { GoogleMapsProvider } from './components/GoogleMapsProvider';

// Auth pages -- login/signup/verify-email live in PublicRouteElements now;
// CreatePassword is still used directly below for the /create-password route.
import { CreatePassword } from './pages/Auth/components';

// Onboarding pages (barreled via pages/OnBoarding/index.ts)
import { Onboarding, OnboardingSteps, CreateAccount } from './pages/OnBoarding';

// Home always renders pages/HomeV2 (pages/Home is kept only as a fallback).
// CookieBanner comes straight from the file, not the barrel -- the barrel would
// pull a Home tree in and with it that tree's stylesheet.
import HomeRoute from './pages/HomeRoute';
import CookieBanner from './pages/HomeV2/CookieBanner';

// Settings pages (barreled via pages/Settings/index.ts)
import {
  Settings,
  SettingsTeamBasedProfile,
  CompanyInfo,
  NotificationList,
  AIworkflow,
  AIControls,
  AlertThresholds,
  AlertCustomization,
  RolesAndPermissions,
  TransportProviders,
  SettingsTeams,
  AgentsTable,
  SettingsProductTable,
  Sensors,
  Credentials,
  FreightMarkupRates,
  SettingsLocations,
  SettingsUserTable,
  GxPReportPage,
} from './pages/Settings';

// Airport pages (barreled via pages/Airports/index.ts)
import {
  AirlineDetail,
  AirlineList,
  AirportList,
  CustomsOffice,
} from './pages/Airports';

// Shipment pages -- kept as individual imports rather than a barrel:
import Shipments from './pages/Shipments/ControlTower';
import LanePlannerPage from './pages/Shipments/LanePlanner';
import DevQuickstatBookingCompletePage from './pages/Shipments/DevQuickstatBookingCompletePage';
import MapExternalView from './pages/Shipments/MapExternalView';
import MonitorPage, { MonitorGlobePage } from './pages/Monitor/MonitorPage';
import GxPReportGenrationPage from './pages/Shipments/GxPReportGeneration/GxPReportGenrationPage';

// Standalone pages -- each lives in a single-file folder, so a barrel
// wouldn't reduce the import count (still one line each either way).
import Confirmation from './pages/Confirmation';
import CompanyContacts from './pages/company-contacts/page';
import Logiblocks from '@/pages/Logiblocks/Logiblocks';
import AIHistoryPage from './pages/AiHistory/AIHistoryPage';
import InventoryPage from './pages/Inventory/InventoryPage';

// Theme
import { zoomLogiAntdDarkTheme } from './theme/antdDarkTheme';

// Types / utils
import { ProfileData } from './apis/settings';
import { isZoomlogiSupport } from './common/utils/utils';
import { mapLanguageToCode } from './common/utils/languageMapper';

/**
 * Main application component that sets up routing and user session management
 */
const App: React.FC = () => {
  const { token, userID, removeToken, decodedUserToken } = useToken();
  const [userData, setUserData] = useState<ProfileData>({});
  const companyName = decodedUserToken()?.company_name;
  const { i18n } = useTranslation();
  const darkModeEnabled = useAppearanceSync();

  // Map language codes to Ant Design locales
  const antdLocales: Record<string, typeof enUS> = {
    en: enUS,
    de: deDE,
    ja: jaJP,
    fr: frFR,
    ko: koKR,
  };

  // Get current locale or fallback to English
  const currentLocale = antdLocales[i18n.language] || enUS;

  // Set language from JWT token on login
  useEffect(() => {
    if (token) {
      const decoded = decodedUserToken();
      if (decoded?.preferred_language) {
        const languageCode = mapLanguageToCode(decoded.preferred_language);
        if (i18n.language !== languageCode) {
          const _ = i18n.changeLanguage(languageCode);
        }
      }
    }
  }, [decodedUserToken, i18n, token]);

  useEffect(() => {
    if (FullStory && userData) {
      FullStory('setProperties', {
        type: 'user',
        properties: {
          displayName: userData && userData?.name,
          email: userData && userData?.email,
        },
      });
    }
  }, [userData]);

  useEffect(() => {
    if (token && FullStory) {
      FullStory('setIdentity', {
        uid: userID ?? undefined,
        properties: {
          displayName: userData && userData?.name,
          email: userData && userData?.email,
        },
      });
    }
  }, [token, userData, userID]);

  // Initialize AI call status WebSocket for real-time notifications
  useAICallStatusSocket();

  // Idle-session logout -- scoped to Cencora's white-label domain, no-op elsewhere
  useIdleLogout(!!token);

  // Synchronize complete storage/auth resets across multiple browser tabs
  useCrossTabStorageSync();

  const {
    data: profileQueryData,
    isError: profileQueryIsError,
    error: profileQueryError,
  } = useProfileQuery(!!token);

  useEffect(() => {
    // ApiResponse<T> nests three levels deep: data.data.data === T,
    // with is_success/message living alongside that innermost `data`.
    const outer = profileQueryData?.data ? profileQueryData.data : null;
    const inner = outer?.data ? outer.data : null;
    if (!inner) return;
    if (inner?.is_success) {
      const resp = inner?.data ? inner.data : null;
      if (resp) setUserData(resp);
    } else {
      message.error(inner?.message);
    }
  }, [profileQueryData]);

  useEffect(() => {
    if (!profileQueryIsError) return;
    const status = (profileQueryError as { status?: number } | undefined)
      ?.status;
    if (status === 401) removeToken();
  }, [profileQueryIsError, profileQueryError, removeToken]);

  return (
    <ConfigProvider
      locale={currentLocale}
      theme={
        darkModeEnabled
          ? zoomLogiAntdDarkTheme
          : {
              algorithm: antdThemeAlgorithms.defaultAlgorithm,
              token: {
                // AiInsightsChatModal portal uses z-index 1040 (above map/HUD ≤1000). Modal and
                // Drawer masks use this seed in antd’s generated CSS; the default 1000 would sit
                // under the chat. Keep this strictly above 1040 so confirms stay visible.
                zIndexPopupBase: 1100,
              },
            }
      }
    >
      <CookieBanner></CookieBanner>
      <section className='main-container'>
        <GoogleMapsProvider>
          <Routes>
            {/* guest routes  */}
            <Route element={<PublicRoutes />}>{PublicRouteElements}</Route>
            <Route path='/forgot-password' element={<ForgotPasswordRoutes />} />
            <Route
              path='/create-password'
              element={
                <Onboarding>
                  <CreatePassword />
                </Onboarding>
              }
            />

            <Route element={<PrivateRoutes />}>
              <Route
                path='/confirmation'
                element={
                  isZoomlogiSupport(companyName) ? (
                    <Navigate to='/' replace />
                  ) : (
                    <Onboarding>
                      <Confirmation />
                    </Onboarding>
                  )
                }
              />
              <Route
                path='/airport-list'
                element={
                  <DashboardLayout>
                    <AirportList />
                  </DashboardLayout>
                }
              />
              <Route
                path='/airport-list/airport/:code'
                element={
                  <DashboardLayout>
                    <AirlineList />
                  </DashboardLayout>
                }
              />
              <Route
                path='/airport-list/airport/airline-detail/:airlineName'
                element={
                  <DashboardLayout>
                    <AirlineDetail />
                  </DashboardLayout>
                }
              />
              <Route
                path='/airport-list/:airlineName/company-contact'
                element={
                  <DashboardLayout>
                    <CompanyContacts />
                  </DashboardLayout>
                }
              />
              <Route
                path='/airport-list/:code/customs'
                element={
                  <DashboardLayout>
                    <CustomsOffice />
                  </DashboardLayout>
                }
              />

              {/* //wills show to all */}
              <Route
                path='/dashboard'
                element={
                  <DashboardLayout>
                    <HomeRoute />
                  </DashboardLayout>
                }
              />
              <Route
                path='/settings'
                element={
                  <DashboardLayout>
                    <Settings>
                      <Outlet />
                    </Settings>
                  </DashboardLayout>
                }
              >
                <Route index element={<Navigate to='profile' replace />} />
                <Route
                  path='profile'
                  element={<SettingsTeamBasedProfile title='My profile' />}
                />
                <Route
                  path='company-info'
                  element={<CompanyInfo title='Company info' />}
                />
                <Route path='notification' element={<NotificationList />} />
                <Route
                  path='aiworkflow'
                  element={<AIworkflow title='AI workflow' />}
                />
                <Route
                  path='ai-controls'
                  element={<AIControls title='AI controls' />}
                />
                <Route
                  path='threshold'
                  element={<AlertThresholds title='Alert thresholds' />}
                />
                <Route
                  path='alert-customization'
                  element={<AlertCustomization title='Alert Customization' />}
                />
                <Route path='roles' element={<RolesAndPermissions />} />
                <Route
                  path='couriers'
                  element={<TransportProviders title='Transport Providers' />}
                />
                <Route path='team' element={<SettingsTeams />} />
                <Route path='ai-agent' element={<AgentsTable />} />
                <Route path='product' element={<SettingsProductTable />} />
                <Route path='sensor' element={<Sensors title='Sensors' />} />
                <Route
                  path='credentials'
                  element={<Credentials title='Credentials' />}
                />
                <Route
                  path='freight-markup-rates'
                  element={<FreightMarkupRates />}
                />
                <Route
                  path='location'
                  element={<SettingsLocations title='Locations' />}
                />
                <Route
                  path='user'
                  element={<SettingsUserTable title='Users' />}
                />
                <Route
                  path='gxp-reports'
                  element={<GxPReportPage title={'Reports'} />}
                />
              </Route>
              <Route
                path='/shipment/external-view/:id'
                element={<MapExternalView />}
              />
              <Route path='/monitor' element={<MonitorPage />} />
              <Route path='/monitor_v2' element={<MonitorGlobePage />} />
              <Route
                path='/shipment'
                element={
                  <DashboardLayout>
                    <Shipments />
                  </DashboardLayout>
                }
              />
              <Route
                path='/lane-planner'
                element={
                  <DashboardLayout>
                    <LanePlannerPage />
                  </DashboardLayout>
                }
              />

              {import.meta.env.DEV ? (
                <Route
                  path='/dev/quickstat-booking-complete'
                  element={<DevQuickstatBookingCompletePage />}
                />
              ) : null}

              <Route
                path='/ai-history'
                element={
                  <DashboardLayout>
                    <AIHistoryPage title='AI history' />
                  </DashboardLayout>
                }
              />
              <Route
                path={AppRoutes.LOGIBLOCKS}
                element={
                  <DashboardLayout>
                    <Logiblocks />
                  </DashboardLayout>
                }
              />
              <Route
                path='/create-account'
                element={
                  <Onboarding>
                    <CreateAccount />
                  </Onboarding>
                }
              />
              <Route
                path='/onboarding'
                element={
                  <Onboarding>
                    <OnboardingSteps />
                  </Onboarding>
                }
              />
              <Route
                path='/publish-gxp-report/:id'
                element={
                  <DashboardLayout>
                    <GxPReportGenrationPage />
                  </DashboardLayout>
                }
              />

              <Route
                path='/inventory'
                element={
                  <DashboardLayout>
                    <InventoryPage title='Inventory' />
                  </DashboardLayout>
                }
              />
            </Route>

            <Route
              path='/share/shipment/external-view/:id/:uuid'
              element={<MapExternalView />}
            />
            {/* Default route */}

            {/* dummy rout to access page  */}
            <Route path='*' element={<Navigate to='/login' replace />} />
          </Routes>
        </GoogleMapsProvider>
      </section>
    </ConfigProvider>
  );
};

export default App;
